/**
 * Application Item Drawer
 * 
 * Side panel for creating/editing applications
 * Features:
 * - Add/Edit mode
 * - Form validation with duplicate name check
 * - Control Activities multi-select picker
 */

import React, { useState, useEffect } from 'react';
import { Form, Input, Button } from 'antd';
import styled from 'styled-components';
import RightContentDrawer from '../../components/common/RightContentDrawer';
import ButtonGroup from '../../components/common/ButtonGroup';

// ========== TYPE DEFINITIONS ==========

export interface AssertionFormData {
  id?: string;
  name: string;
}

interface AssertionItemDrawerProps {
  open: boolean;
  mode: 'add' | 'edit';
  initialValues?: AssertionFormData | null;
  existingNames?: string[]; // For duplicate name validation
  onClose: () => void;
  onSubmit: (data: AssertionFormData) => Promise<void>;
}

// ========== STYLED COMPONENTS ==========

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  .ant-form-item {
    margin-bottom: 0 !important;
  }
`;

const FormItem = styled.div`
  margin-bottom: 20px !important;
`;

const FormLabel = styled.label`
  display: block;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #374151;
  margin-bottom: 8px;
`;

const RequiredMark = styled.span`
  color: #DC2626;
  margin-left: 4px;
`;

const StyledInput = styled(Input)`
  height: 44px;
  border-radius: 8px;
  font-family: Inter;
  font-size: 14px;
  
  &:focus, &:hover {
    border-color: #2563EB;
  }
`;

// ========== COMPONENT ==========

const AssertionItemDrawer: React.FC<AssertionItemDrawerProps> = ({
  open,
  mode,
  initialValues,
  existingNames = [],
  onClose,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

 
  // Reset form when drawer opens/closes or initialValues change
  useEffect(() => {
    if (open) {
      if (initialValues) {
        form.setFieldsValue({
          name: initialValues.name,

        });
      } else {
        form.resetFields();
      }
    }
  }, [open, initialValues, form]);
  
  // Validate duplicate name
  const validateName = (_: unknown, value: string) => {
    if (!value || value.trim() === '') {
      return Promise.reject(new Error('Please enter application name'));
    }
    
    const trimmedValue = value.trim().toLowerCase();
    const currentName = initialValues?.name?.trim().toLowerCase();
    
    // In edit mode, allow the current name
    const isDuplicate = existingNames
      .filter(name => mode === 'edit' ? name.toLowerCase() !== currentName : true)
      .some(name => name.toLowerCase() === trimmedValue);
    
    if (isDuplicate) {
      return Promise.reject(new Error('An assertion with this name already exists'));
    }
    
    return Promise.resolve();
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      
      const formData: AssertionFormData = {
        ...values,
      };
      
      await onSubmit(formData);
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setSubmitting(false);
    }
  };
 

  const footer = (
    <ButtonGroup>
      <Button type="default" onClick={onClose} disabled={submitting}>
        Go back
      </Button>
      <Button
        type="primary"
        onClick={handleSubmit}
        loading={submitting}
      >
        {mode === 'add' ? 'Add item' : 'Save changes'}
      </Button>
    </ButtonGroup>
  );
  
  return (
    <RightContentDrawer
      open={open}
      title={mode === 'add' ? 'Add Assertion' : 'Edit Assertion'}
      onClose={onClose}
      footer={footer}
      width={520}
    >
      <FormWrapper>
        <Form form={form} layout="vertical">
          {/* Name */}
          <FormItem>
            <FormLabel>
              Assertion Name
              <RequiredMark>*</RequiredMark>
            </FormLabel>
            <Form.Item
              name="name"
              rules={[{ validator: validateName }]}
              style={{ marginBottom: 0 }}
            >
              <StyledInput placeholder="e.g., Access, Accuracy, etc." />
            </Form.Item>
          </FormItem>

        </Form>
      </FormWrapper>
    </RightContentDrawer>
  );
};

export default AssertionItemDrawer;
